import React from 'react';
import { Box, Typography, CircularProgress, Button, Chip, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  buildPriorTimeline,
  formatPriorStudyDateTime,
  getRecommendedPrior,
  KAIROS_PATIENT_TIMELINE_RECOMMENDED_PRIOR,
  getPriorTimelineCell,
  normalizePriorModalities,
} from './priorTimeline';

const modalityChips = (modalities = '') => normalizePriorModalities(modalities);

const cellKey = (cell) => cell ? `${cell.modality}:${cell.year}` : '';
const studyUid = (study = {}) => study.studyInstanceUID || study.study_instance_uid || '';
const EMPTY_SELECTED_CELLS = [];

const priorStudyDate = (study = {}) => study.studyDate || study.study_date || '';
const priorStudyTime = (study = {}) => study.studyTime || study.study_time || '';
const priorStudyYear = (study = {}) => String(priorStudyDate(study) || '').slice(0, 4) || 'undated';
const priorModalityLabel = (study = {}) => modalityChips(study.modalities || study.modality || '').join('/') || 'STUDY';
const shortStudyUid = (study = {}) => {
  const uid = studyUid(study);
  if (!uid) return '';
  return uid.length > 10 ? `…${uid.slice(-10)}` : uid;
};
export const priorCompareLabel = (study, index = 0, total = 1) => `COMPARE ${index + 1}/${total} · ${priorModalityLabel(study)} ${priorStudyYear(study)}`;
export const priorIdentityLabel = (study = {}) => [
  `${priorModalityLabel(study)} ${priorStudyYear(study)}`,
  formatPriorStudyDateTime(priorStudyDate(study), priorStudyTime(study)).slice(0, 10),
  study.studyDescription || study.accessionNumber || shortStudyUid(study),
].filter(Boolean).join(' · ');
export const buildPriorCompareOptions = (study, index, total, cells = []) => ({
  newTab: true,
  compare: {
    label: priorCompareLabel(study, index, total),
    bucket: `${priorModalityLabel(study)} ${priorStudyYear(study)}`,
    identity: priorIdentityLabel(study),
    index: index + 1,
    total,
    modality: priorModalityLabel(study),
    year: priorStudyYear(study),
    studyDate: priorStudyDate(study),
    accessionNumber: study.accessionNumber || '',
    studyDescription: study.studyDescription || '',
    cells,
  },
});



function RecommendedPriorBanner({ recommendation, onOpenPrior }) {
  if (!recommendation?.study) return null;
  const prior = recommendation.study;
  const uid = studyUid(prior);
  const tags = recommendation.tags?.length ? recommendation.tags : ['LONGITUDINAL'];
  return (
    <Box
      data-kairos-marker={KAIROS_PATIENT_TIMELINE_RECOMMENDED_PRIOR}
      role="region"
      aria-label={`Recommended prior: ${priorIdentityLabel(prior)}`}
      sx={{ mx: 1, mt: 1, mb: 0.75, p: 0.85, border: '1px solid rgba(255,235,59,0.35)', bgcolor: 'rgba(255,235,59,0.045)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.025)' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.55 }}>
        <Chip label="BEST PRIOR" size="small" sx={{ height: 18, fontSize: 8.5, borderRadius: 0, bgcolor: 'rgba(255,235,59,0.16)', color: '#fff59d', fontWeight: 800, letterSpacing: 0.5 }} />
        {tags.slice(0, 3).map(tag => (
          <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: 8.5, borderRadius: 0, bgcolor: 'rgba(0,188,212,0.12)', color: tag === 'RECENT' ? '#f8bbd0' : '#80deea', fontWeight: 700 }} />
        ))}
      </Box>
      <Typography title={priorIdentityLabel(prior)} sx={{ fontSize: 10, color: '#d8d0a0', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mb: 0.35 }}>
        {priorIdentityLabel(prior)}
      </Typography>
      <Typography sx={{ fontSize: 9, color: '#aab6bc', lineHeight: 1.25, mb: 0.65 }}>
        Auto-suggested prior — verify before comparing.
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.6 }}>
        <Button type="button" size="small" variant="outlined" aria-label={`Open recommended prior ${priorIdentityLabel(prior)}`} onClick={() => onOpenPrior(uid)} sx={{ minHeight: 22, py: 0, px: 0.75, fontSize: 9, borderRadius: 0, color: '#fff59d', borderColor: 'rgba(255,235,59,0.35)' }}>
          Open best prior
        </Button>
        <Button type="button" size="small" variant="outlined" aria-label={`Compare recommended prior ${priorIdentityLabel(prior)}`} onClick={() => onOpenPrior(uid, buildPriorCompareOptions(prior, 0, 1))} sx={{ minHeight: 22, py: 0, px: 0.75, fontSize: 9, borderRadius: 0, color: '#00bcd4', borderColor: '#174852' }}>
          Compare best prior
        </Button>
      </Box>
    </Box>
  );
}

export function SmartPriorTimeline({ priors, compact = false, onOpenPrior, onComparePriors = null, initialSelectedCells = EMPTY_SELECTED_CELLS }) {
  const timeline = React.useMemo(() => buildPriorTimeline(priors), [priors]);
  const [selectedCells, setSelectedCells] = React.useState(initialSelectedCells || []);
  const selectedCellSet = React.useMemo(() => new Set(selectedCells), [selectedCells]);
  const selectedStudies = React.useMemo(() => {
    const seen = new Set();
    const studies = [];
    selectedCells.forEach(key => {
      const [modality, year] = key.split(':');
      getPriorTimelineCell(timeline, modality, year).forEach(study => {
        const uid = studyUid(study);
        if (uid && !seen.has(uid)) {
          seen.add(uid);
          studies.push(study);
        }
      });
    });
    return studies.sort((a, b) => `${priorStudyDate(b)}${priorStudyTime(b)}`.localeCompare(`${priorStudyDate(a)}${priorStudyTime(a)}`));
  }, [timeline, selectedCells]);

  const forcedSelectedCells = React.useMemo(() => initialSelectedCells || [], [initialSelectedCells]);
  React.useEffect(() => {
    if (forcedSelectedCells.length > 0) setSelectedCells(forcedSelectedCells);
  }, [timeline, forcedSelectedCells]);

  const forcedCells = React.useMemo(() => forcedSelectedCells.map(key => { const [modality, year] = key.split(':'); return { modality, year }; }).filter(cell => cell.modality && cell.year), [forcedSelectedCells]);
  const displayYears = React.useMemo(() => Array.from(new Set([...timeline.years, ...forcedCells.map(cell => cell.year)])).sort(), [timeline.years, forcedCells]);
  const displayModalities = React.useMemo(() => Array.from(new Set([...timeline.modalities, ...forcedCells.map(cell => cell.modality)])).sort(), [timeline.modalities, forcedCells]);

  if (!displayYears.length || !displayModalities.length) return null;

  const toggleCell = (cell) => {
    const key = cellKey(cell);
    setSelectedCells(prev => prev.includes(key)
      ? prev.filter(item => item !== key)
      : [...prev, key]
    );
  };
  const handleCellKeyDown = (event, cell) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleCell(cell);
    }
  };

  const isSelected = (modality, year) => selectedCellSet.has(`${modality}:${year}`);
  const clearSelectedCells = () => setSelectedCells([]);

  return (
    <Box sx={{ p: 1, borderBottom: '1px solid #171717', bgcolor: '#07090a' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.75 }}>
        <Typography sx={{ fontSize: 10, color: '#80deea', fontWeight: 700, letterSpacing: 1 }}>
          SMART TIMELINE
        </Typography>
        <Typography sx={{ fontSize: 9, color: '#52666c' }}>
          modality × year · select priors
        </Typography>
        {selectedStudies.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (onComparePriors) {
                onComparePriors(selectedStudies, selectedCells);
                return;
              }
              selectedStudies.forEach((study, index) => onOpenPrior(study.studyInstanceUID, buildPriorCompareOptions(study, index, selectedStudies.length, selectedCells)));
            }}
            sx={{ ml: 'auto', minHeight: 20, py: 0, px: 0.75, fontSize: 9, borderRadius: 0, color: '#00bcd4', borderColor: '#174852' }}
          >
            Compare selected ({selectedStudies.length})
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `34px repeat(${displayYears.length}, minmax(30px, 1fr))`,
          gap: '5px 7px',
          alignItems: 'center',
          userSelect: 'none',
        }}
        data-testid="prior-timeline-grid"
      >
        <Box />
        {displayYears.map(year => (
          <Typography key={year} sx={{ fontSize: 9, color: '#92a5aa', fontFamily: 'monospace', textAlign: 'center' }}>
            {year}
          </Typography>
        ))}
        {displayModalities.map(modality => (
          <React.Fragment key={modality}>
            <Typography sx={{ fontSize: 10, color: '#d5f7ff', fontFamily: 'monospace' }}>
              {modality}
            </Typography>
            {displayYears.map(year => {
              const studies = getPriorTimelineCell(timeline, modality, year);
              const active = studies.length > 0;
              const selected = isSelected(modality, year);
              const latest = studies[0];
              const tooltip = active
                ? `${modality} ${year}: ${studies.length} stud${studies.length === 1 ? 'y' : 'ies'} — click to ${selected ? 'remove from' : 'add to'} comparison: ${latest?.studyDescription || latest?.accessionNumber || 'prior'}`
                : `${modality} ${year}: no studies`;
              const cell = { modality, year };
              return (
                <Tooltip key={`${modality}:${year}`} title={tooltip} placement="top">
                  <Box
                    component="button"
                    type="button"
                    aria-label={tooltip}
                    aria-pressed={active ? selected : undefined}
                    aria-disabled={!active}
                    tabIndex={active ? 0 : -1}
                    onClick={(e) => { if (active) { e.preventDefault(); toggleCell(cell); } }}
                    onKeyDown={(e) => { if (active) handleCellKeyDown(e, cell); }}
                    sx={{
                      height: compact ? 18 : 22,
                      minWidth: 0,
                      border: selected ? '2px solid #ffeb3b' : active ? '1px solid #1e6875' : '1px solid #151515',
                      bgcolor: selected ? 'rgba(255,235,59,0.28)' : active ? 'rgba(0,188,212,0.14)' : '#090909',
                      color: selected ? '#fff59d' : active ? '#80deea' : '#2a2a2a',
                      borderRadius: '999px',
                      cursor: active ? 'pointer' : 'default',
                      fontSize: 9,
                      fontFamily: 'monospace',
                      p: 0,
                      '&:hover': active ? { borderColor: '#00bcd4', bgcolor: 'rgba(0,188,212,0.26)' } : {},
                      '&[aria-disabled="true"]': { color: '#2a2a2a' },
                    }}
                  >
                    {active ? `${selected ? '✓ ' : ''}${studies.length}` : selected ? '✓' : '·'}
                  </Box>
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      {selectedStudies.length > 0 && (
        <Box aria-live="polite" sx={{ mt: 0.75, border: '1px solid #2a2610', bgcolor: 'rgba(255,235,59,0.055)', px: 0.75, py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
            <Typography sx={{ fontSize: 9, color: '#fff59d', fontWeight: 700, letterSpacing: 0.8 }}>
              SELECTED PRIORS
            </Typography>
            <Typography sx={{ fontSize: 9, color: '#9b9564', fontFamily: 'monospace' }}>
              {selectedStudies.length === 1 ? '1 study' : `${selectedStudies.length} studies`}
            </Typography>
            <Button
              size="small"
              variant="text"
              aria-label="Clear selected priors"
              onClick={clearSelectedCells}
              sx={{ ml: 'auto', minHeight: 18, py: 0, px: 0.6, fontSize: 8.5, borderRadius: 0, color: '#b8b078' }}
            >
              Clear
            </Button>
          </Box>
          {selectedStudies.slice(0, compact ? 2 : 4).map((study, index) => (
            <Typography key={studyUid(study) || index} title={`${priorCompareLabel(study, index, selectedStudies.length)} — ${priorIdentityLabel(study)}`} sx={{ fontSize: 9, color: '#d8d0a0', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 }}>
              {priorCompareLabel(study, index, selectedStudies.length)} — {priorIdentityLabel(study)}
            </Typography>
          ))}
          {selectedStudies.length > (compact ? 2 : 4) && (
            <Typography sx={{ fontSize: 9, color: '#8a8a62', fontFamily: 'monospace' }}>
              +{selectedStudies.length - (compact ? 2 : 4)} more selected
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function PriorsPanel({ priors, loading, error, onOpenPrior, onComparePriors = null, compact = false, initialSelectedCells = EMPTY_SELECTED_CELLS, studyInfo = null }) {
  const recommendedPrior = React.useMemo(() => getRecommendedPrior(priors, studyInfo), [priors, studyInfo]);
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0b0b0b', borderLeft: '1px solid #1e1e1e' }}>
      <Box sx={{ px: 1.25, py: compact ? 0.75 : 1, borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <HistoryIcon sx={{ fontSize: 15, color: '#00bcd4' }} />
        <Typography sx={{ fontSize: 11, color: '#00bcd4', fontWeight: 700, letterSpacing: 1.5 }}>
          PRIORS
        </Typography>
        {!loading && !error && (
          <Typography sx={{ ml: 'auto', fontSize: 10, color: '#555', fontFamily: 'monospace' }}>
            {priors.length}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 1.25 }}>
            <Typography sx={{ color: '#ff6b6b', fontSize: 11, lineHeight: 1.4 }}>
              Could not load priors.
            </Typography>
            <Typography sx={{ color: '#666', fontSize: 10, mt: 0.5, wordBreak: 'break-word' }}>
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && priors.length === 0 && (
          <Typography sx={{ color: '#666', fontSize: 11, px: 1.25, py: 2, lineHeight: 1.4 }}>
            No same-patient prior studies found.
          </Typography>
        )}

        {!loading && !error && priors.length > 0 && (
          <>
            <RecommendedPriorBanner recommendation={recommendedPrior} onOpenPrior={onOpenPrior} />
            <SmartPriorTimeline priors={priors} compact={compact} onOpenPrior={onOpenPrior} onComparePriors={onComparePriors} initialSelectedCells={initialSelectedCells} />
          </>
        )}

        {!loading && !error && !compact && priors.map(prior => (
          <Box
            key={prior.studyInstanceUID}
            sx={{ p: 1.1, borderBottom: '1px solid #171717', '&:hover': { bgcolor: '#101b1f' } }}
          >
            <Typography sx={{ color: '#d5d5d5', fontSize: 11, fontFamily: 'monospace', mb: 0.4 }}>
              {formatPriorStudyDateTime(prior.studyDate, prior.studyTime)}
            </Typography>
            <Box sx={{ mb: 0.6 }}>
              {modalityChips(prior.modalities).map(m => (
                <Chip
                  key={m}
                  label={m}
                  size="small"
                  sx={{ height: 17, fontSize: 9, mr: 0.35, mb: 0.35, borderRadius: 0, bgcolor: '#10222a', color: '#80deea', fontWeight: 700 }}
                />
              ))}
            </Box>
            <Tooltip title={prior.studyDescription || prior.accessionNumber || 'Prior study'} placement="left">
              <Typography sx={{ color: '#aaa', fontSize: 11, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {prior.studyDescription || prior.accessionNumber || 'No description'}
              </Typography>
            </Tooltip>
            {prior.accessionNumber && (
              <Typography sx={{ color: '#666', fontSize: 10, mt: 0.35, fontFamily: 'monospace' }}>
                ACC {prior.accessionNumber}
              </Typography>
            )}
            <Typography sx={{ color: '#444', fontSize: 9, mt: 0.35, fontFamily: 'monospace' }}>
              {prior.numSeries} series · {prior.numInstances} images
            </Typography>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
              onClick={() => onOpenPrior(prior.studyInstanceUID)}
              sx={{ mt: 0.9, minHeight: 24, fontSize: 10, py: 0.2, borderRadius: 0, color: '#00bcd4', borderColor: '#174852', '&:hover': { borderColor: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' } }}
            >
              Open prior
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
